import { useEffect, useMemo, useRef } from "react";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  buildVoiceStudyFrontPrompt,
  getVoiceStudyCard,
  useVoiceStudyActiveSession,
  useVoiceStudyTurn,
  type VoiceStudySessionHandle,
  type VoiceStudyTurn,
} from "@-/dev.improve.haniverse.lib.voice-study";
import { getPinyinMp3Asset } from "@-/dev.improve.haniverse.assets.pinyin.audio";
import { tonelessSyllableForPinyinAssetLookup } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import {
  DrillPageContext,
  GraphemeDrillContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import {
  useSubmitAnswer,
  useIsCorrectAnswer,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.submit";
import {
  useRevealMutation,
  useIsRevealing,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { persistentKeyboardDismiss } from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import useLatest from "@-/com.npmjs.use-latest";

/** A grapheme drill's front as a voice-study turn, while voice study is on. */
export function GraphemeVoiceStudyDrill() {
  const session = useVoiceStudyActiveSession();
  return session !== null && <ActiveGraphemeVoiceStudyDrill session={session} />;
}

/**
 * A word drill's front under voice study. Voice study only drills a grapheme's meaning, so a word
 * card is never eligible: it is skipped (advanced without a rating) as soon as it is the live,
 * focused surface.
 */
export function WordVoiceStudyDrill() {
  const session = useVoiceStudyActiveSession();
  return session !== null && <ActiveWordVoiceStudyDrill />;
}

/**
 * Advance past a card voice study cannot drill — WITHOUT a domain write: an ineligible card is
 * skipped, never rated. Also drops the keyboard while voice drives the live front. The deck
 * promotes the next card, which remounts the caller and clears the guard. Only called while a
 * session is active (its callers mount exactly then).
 */
function useVoiceStudyFrontSkip({
  character,
  eligible,
  focused,
}: {
  character: string;
  eligible: boolean;
  focused: boolean;
}) {
  const [commit] = useContextSelectors(LearnDeckContext, (c) => c.commit);
  const skipping = useRef(false);
  const skipRef = useLatest(() => {
    commit({ kind: "continue" }, "voice");
  });

  useEffect(() => {
    if (focused) {
      persistentKeyboardDismiss();
    }
    if (!focused || eligible || skipping.current) {
      return;
    }
    skipping.current = true;
    skipRef.current();
  }, [focused, eligible, character, skipRef]);

  return skipRef;
}

function ActiveWordVoiceStudyDrill() {
  const focused = useIsFocused();
  const [character] = useContextSelectors(DrillPageContext, (c) => c.character);
  useVoiceStudyFrontSkip({ character, eligible: false, focused });
  return null;
}

// Touch study does not need voice mutations, pinyin queries, or a published turn. Keeping
// those subscriptions mounted made focus changes clear/re-register a disabled voice prompt.
function ActiveGraphemeVoiceStudyDrill({ session }: { session: VoiceStudySessionHandle }) {
  const focused = useIsFocused();
  const [character] = useContextSelectors(DrillPageContext, (c) => c.character);
  const [card, drillType] = useContextSelectors(
    GraphemeDrillContext,
    (c) => c.slice.data,
    (c) => c.drillType,
  );
  const [setAnswerInputState] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.setAnswerInputState,
  );
  const submitMutation = useSubmitAnswer();
  const revealMutation = useRevealMutation();
  const revealing = useIsRevealing();
  const isCorrect = useIsCorrectAnswer();
  const clue = drillType.kind === "meaning" ? getVoiceStudyCard(card) : null;
  const eligible = clue !== null;
  const skipRef = useVoiceStudyFrontSkip({ character, eligible, focused });

  // The same reading the reveal audio would play; the recorded clip says the pinyin when we have it.
  const studyPinyinList = usePrevalentPinyin<{ toneNumbersPinyin: string }>(
    character,
    card.pinyinList,
  );
  const pinyinKey = studyPinyinList[0]?.toneNumbersPinyin;
  const clip = useMemo(
    () => (pinyinKey === undefined ? null : getPinyinMp3Asset(pinyinKey)),
    [pinyinKey],
  );
  const syllable = pinyinKey === undefined ? "" : tonelessSyllableForPinyinAssetLookup(pinyinKey);
  const prompt = useMemo(
    () => (clue ? buildVoiceStudyFrontPrompt({ card: clue, clip, syllable }) : null),
    [clip, clue, syllable],
  );

  const turn: VoiceStudyTurn | null =
    clue && prompt
      ? {
          glyph: character,
          id: `${character}:meaning:front`,
          onCommand: async (command) => {
            if (!focused || revealing || submitMutation.isPending) {
              return { kind: "handled" };
            }
            // A real back such as “good” or “back” outranks the same-named command on the front.
            if (command.text.length > 0 && isCorrect(command.text)) {
              // Echo the transcript verbatim so the user hears exactly what was recognized.
              await session.say({ text: `${command.text}. Right.` });
              submitMutation.setInputOverride(command.text);
              await submitMutation.mutateAsync();
              return { kind: "handled" };
            }
            switch (command.kind) {
              case "answer": {
                // Stay on the front: the submit mutation would reveal with Again, as typing does.
                setAnswerInputState((previous) => ({
                  ...previous,
                  input: command.text,
                  validationState: "incorrect",
                }));
                return { kind: "feedback", prompt: { text: `${command.text}. Wrong.` } };
              }
              case "next":
              case "reveal": {
                await revealMutation.mutateAsync();
                return { kind: "handled" };
              }
              case "skip": {
                skipRef.current();
                return { kind: "handled" };
              }
              case "rating": {
                return {
                  kind: "feedback",
                  prompt: {
                    text: "Say the meaning, or say tap to reveal before rating this card.",
                  },
                };
              }
              case "unreveal": {
                return { kind: "handled" };
              }
              default: {
                return { kind: "unhandled" };
              }
            }
          },
          prompt,
          side: "front",
        }
      : null;
  useVoiceStudyTurn(turn);
  return null;
}
